import { Link } from 'react-router-dom';
import { 
  Gift, 
  Sparkles, 
  CheckCircle, 
  Stethoscope,
  Trophy,
  MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SEO } from '../components/SEO';
import { ToothIcon } from '../components/icons/ToothIcon';

const WHATSAPP_NUMBER = "+27714375240";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}`;

export const Promotions = () => {
  return (
    <div className="min-h-screen" data-testid="promotions-page">
      <SEO 
        title="Promotions"
        description="Special healthcare promotions at K&K Medical and Dental. Combo packages, loyalty rewards, and monthly prize draws. Save on medical and dental services in Umzinto."
        keywords="medical promotions Umzinto, dental deals KZN, healthcare discounts, doctor promotions, dentist specials"
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 py-16 lg:py-24 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 text-white text-sm font-semibold mb-6 animate-fade-in-up backdrop-blur-sm">
            <Gift className="w-4 h-4" />
            Limited Time Offers
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6 animate-fade-in-up animation-delay-100">
            Exclusive <span className="text-amber-200">Promotions</span>
          </h1>
          <p className="text-lg sm:text-xl text-white/90 leading-relaxed max-w-3xl mx-auto animate-fade-in-up animation-delay-200">
            Take advantage of our special offers and save on quality healthcare for you and your family.
          </p>
        </div>
      </section>

      {/* Combo Package Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-teal-50 to-cyan-50" data-testid="combo-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Card className="border-0 shadow-2xl rounded-3xl overflow-hidden">
              <div className="bg-gradient-to-r from-teal-600 to-cyan-600 p-8 text-white text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl mb-4">
                  <Sparkles className="w-8 h-8" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold mb-2">Doctor + Dentist Combo</h2>
                <div className="flex items-center justify-center gap-3">
                  <span className="text-2xl font-bold">Get a personalised quote</span>
                </div>
                <p className="text-teal-100 mt-2">Payment upfront</p>
              </div>
              <CardContent className="p-8">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-teal-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-slate-900">1 Doctor Consultation + 1 Dental Consultation</p>
                      <p className="text-slate-600 text-sm">Does not include medication or treatments</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Loyalty Rewards Section */}
      <section className="py-16 lg:py-24 bg-white" data-testid="loyalty-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-2xl mb-6">
              <Gift className="w-8 h-8 text-purple-600" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Loyalty Rewards
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              The more you visit, the more you save. Earn free services just by being a loyal patient.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Dental Cleaning Reward */}
            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-5 text-white">
                <div className="flex items-center gap-3">
                  <ToothIcon className="w-6 h-6" />
                  <h3 className="font-bold text-lg">After 2 Dental Cleanings</h3>
                </div>
              </div>
              <CardContent className="p-5">
                <p className="text-slate-600 mb-4">Get your choice of:</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-blue-500" />
                    <span className="font-medium text-slate-900">Free Neurobion Injection</span>
                  </div>
                  <p className="text-slate-400 text-xs pl-6">OR</p>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-blue-500" />
                    <span className="font-medium text-slate-900">Free Doctor's Consult</span>
                  </div>
                </div>
                <p className="text-slate-500 text-xs mt-4">Redeemable at any time</p>
              </CardContent>
            </Card>

            {/* IV Therapy Reward */}
            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-5 text-white">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-6 h-6" />
                  <h3 className="font-bold text-lg">With Any IV Therapy</h3>
                </div>
              </div>
              <CardContent className="p-5">
                <p className="text-slate-600 mb-4">Get your choice of:</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-purple-500" />
                    <span className="font-medium text-slate-900">Free Neurobion Injection</span>
                  </div>
                  <p className="text-slate-400 text-xs pl-6">OR</p>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-purple-500" />
                    <span className="font-medium text-slate-900">Free Doctor's Consult</span>
                  </div>
                </div>
                <p className="text-slate-500 text-xs mt-4">Redeemable at any time</p>
              </CardContent>
            </Card>

            {/* Doctor Visits Reward */}
            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="bg-gradient-to-r from-teal-500 to-teal-600 p-5 text-white">
                <div className="flex items-center gap-3">
                  <Stethoscope className="w-6 h-6" />
                  <h3 className="font-bold text-lg">After 4 Doctor Visits</h3>
                </div>
              </div>
              <CardContent className="p-5">
                <p className="text-slate-600 mb-4">Receive:</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-teal-500" />
                    <span className="font-medium text-slate-900">Free Dental Cleaning</span>
                  </div>
                </div>
                <p className="text-slate-500 text-xs mt-4">Redeemable at any time</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Monthly Prize Draw Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-amber-50 to-orange-50" data-testid="prize-draw-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <Card className="border-0 shadow-2xl rounded-3xl overflow-hidden">
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-8 text-white text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl mb-4">
                  <Trophy className="w-8 h-8" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold mb-2">Monthly Prize Draw</h2>
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  <span className="text-3xl sm:text-4xl font-bold">Up to R2,000</span>
                  <span className="text-amber-100 text-lg">in prizes monthly</span>
                </div>
              </div>
              <CardContent className="p-8 text-center">
                <p className="text-slate-600 text-lg mb-6">
                  All registered patients (Medical and Dental) are <span className="font-semibold text-slate-900">automatically entered</span> into our monthly prize draw.
                </p>

                <Link
                  to="/prizes"
                  onClick={() => window.scrollTo(0, 0)}
                  className="block group mb-6"
                  data-testid="view-monthly-prizes-link"
                >
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-5 hover:border-amber-400 hover:shadow-lg transition-all duration-300">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="bg-amber-500 rounded-xl p-2.5 group-hover:scale-110 transition-transform">
                          <Gift className="w-5 h-5 text-white" />
                        </div>
                        <div className="text-left">
                          <p className="font-bold text-slate-900">View this month's prizes</p>
                          <p className="text-slate-600 text-sm">See what's up for grabs &amp; full T&amp;Cs</p>
                        </div>
                      </div>
                      <span className="text-amber-600 group-hover:translate-x-1 transition-transform text-2xl">→</span>
                    </div>
                  </div>
                </Link>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
                  <p className="text-amber-800 font-medium">
                    To enter, <a 
                      href={WHATSAPP_LINK}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-amber-600 hover:text-amber-700 underline font-bold inline-flex items-center gap-1"
                      data-testid="whatsapp-entry-link"
                    >
                      click here <MessageCircle className="w-4 h-4" />
                    </a> and type promo.
                  </p>
                </div>

                <Button 
                  asChild
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full px-8 py-6 shadow-lg hover:shadow-xl transition-all duration-300"
                  data-testid="prize-whatsapp-btn"
                >
                  <a 
                    href={WHATSAPP_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Enter via WhatsApp
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-slate-900 to-slate-800" data-testid="promotions-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Don't Miss Out!
          </h2>
          <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
            Visit us today to take advantage of these exclusive offers.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button 
              asChild
              className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              data-testid="promotions-book-btn"
            >
              <Link to="/booking">Book Appointment</Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg transition-all duration-300"
              data-testid="promotions-contact-btn"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
